import { useRouter } from 'next/router';
import { useCallback } from 'react';

import { useFreeToolsInactiveGate } from '../contexts/FreeToolsInactiveGateContext';
import { trackEvent } from '../lib/amplitude';
import { canExportCreatorTools } from '../utils/canExportCreatorTools';
import { useSession } from './useSession';
import { useSoftGate } from './useSoftGate';

type UseCreatorToolDownloadOptions = {
  /** Soft-gate reset when guest dismisses signup without signing in. */
  onSoftGateReset?: () => void;
};

/**
 * Shared download/export gate for standalone creator tools.
 * Returns true when the caller may proceed with the file download.
 */
export function useCreatorToolDownload(options: UseCreatorToolDownloadOptions = {}) {
  const router = useRouter();
  const { isSignedIn, user } = useSession();
  const { openUnlockModal } = useFreeToolsInactiveGate();
  const { triggerSoftGate } = useSoftGate({
    enabled: true,
    onReset: options.onSoftGateReset ?? (() => {}),
  });

  const toolPath = router.pathname;
  const canExport = canExportCreatorTools(user);

  const requestDownload = useCallback((): boolean => {
    if (!isSignedIn) {
      trackEvent('tool_export_blocked', { tool_path: toolPath, reason: 'signed_out' });
      triggerSoftGate();
      return false;
    }

    if (canExportCreatorTools(user)) {
      trackEvent('tool_export_completed', { tool_path: toolPath });
      return true;
    }

    trackEvent('tool_export_blocked', { tool_path: toolPath, reason: 'needs_unlock' });
    openUnlockModal({ entry: 'download', toolPath });
    return false;
  }, [isSignedIn, openUnlockModal, toolPath, triggerSoftGate, user]);

  return {
    canExport,
    requestDownload,
    triggerSoftGate,
    openUnlockModal,
  };
}
